import Backdrop from '@mui/material/Backdrop'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import { useTheme } from '@mui/material/styles'
import { FC, ReactElement } from 'react'

import Grid from '@mui/material/Grid2'
import { UseQueryResult } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { MoovyPalette } from '../../styles/theme'
import { navigateToRootIfNotFoundError } from '../../utils/errorNavigation'
import { MoovyConstantDOMIds } from '../../utils/globalNames'
import MoovyErrorPage from '../moovyErrorPage'
import MoovyMutationError from '../moovyMutationError'
import MoovyProgress from '../moovyProgress'

type MoovyQueryHandlerProps = {
  query: UseQueryResult<any, any>
  rootPageIfNotFoundError: boolean
  children: any
}

const BackdropAnimation = ({ show }: { show: boolean }) => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        opacity: 0.5,
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: theme.zIndex.drawer + 1,
        visibility: show ? 'visible' : 'hidden',
      }}
      id={MoovyConstantDOMIds.MOOVY_BACKDROP_ANIMATION}
    >
      <Backdrop
        sx={{
          backgroundColor: MoovyPalette.colorMainBackground,
        }}
        open={show}
      >
        <MoovyProgress />
      </Backdrop>
    </Box>
  )
}

const ProgressAnimation = () => {
  const getSkeletonAnimations = () => {
    const skeletonCount = 8
    const skeletonItems = []
    for (let i = 0; i < skeletonCount; i++) {
      skeletonItems.push(<Skeleton key={i} animation="wave" />)
    }
    return skeletonItems.map((item) => item)
  }

  return (
    <Grid container gap={2} mt={2}>
      <Grid size={12}>
        <Skeleton variant="rounded" height={60} />
      </Grid>
      <Grid size={12}>{getSkeletonAnimations()}</Grid>
    </Grid>
  )
}

const MoovyQueryHandler: FC<MoovyQueryHandlerProps> = ({
  query,
  rootPageIfNotFoundError,
  children,
}): ReactElement => {
  const navigate = useNavigate()
  if (rootPageIfNotFoundError) {
    navigateToRootIfNotFoundError(navigate, query.error)
  }

  const noDataAnimation = query.isFetching && !query.data
  const dataAnimation = query.isFetching && !!query.data

  return (
    <>
      {query.isError ? (
        <MoovyErrorPage />
      ) : noDataAnimation ? (
        <ProgressAnimation />
      ) : (
        <>
          <BackdropAnimation show={dataAnimation} />
          <>{children}</>
        </>
      )}
      <MoovyMutationError
        query={query}
        errorTitleKey={'common.actions.get.error'}
      />
    </>
  )
}

export default MoovyQueryHandler
